// @ts-nocheck

import ReactDOM from "react-dom";
import { i18n } from "@lingui/core";
import * as Sentry from "@sentry/react";
import React, { useEffect } from "react";
import { spawn } from "redux-saga/effects";
import { I18nProvider } from "@lingui/react";
import { LANGUAGE } from "locales/languages";
import { MainRouter } from "routs/MainRouter";
import { sagaMiddleware, store } from "./model";
import { en, uz, ru } from "make-plural/plurals";
import * as serviceWorker from "./serviceWorker";
import { useCheckAuth } from "hooks/useCheckAuth";
import { edmIsEnabled } from "./utils/edmIsEnabled";
import { YMInitializer } from "react-yandex-metrika";
import { ISystemState } from "./model/system/systemReducer";
import { languageAC } from "./model/system/systemActionCreators";
import { Provider, useDispatch, useSelector } from "react-redux";
import { dEncashSetDataWatcher } from "./model/dashboardEncash/dEncashSaga";
import { dRefundsSetDataWatcher } from "./model/dashboardRefunds/dRefundsSaga";
import { dSalesArchiveSetDataWatcher } from "./model/dashboardSalesArchive/saga";
import { dOrdersArchiveSetDataWatcher } from "./model/dashboardOrdersArchive/saga";
import { dCashDesksSetDataWatcher } from "./model/dashboardCashDesks/dCashDescsSaga";
import { dRefundsArchiveSetDataWatcher } from "./model/dashboardRefundsArchive/saga";
import { dHRecordsSetDataWatcher } from "./model/dashboardHandoverRecord/dHRecordSaga";
import { dTransferCashDesksSetDataWatcher } from "./model/dashboardTransferCashDesks/dTransferCashDesksSaga";
import {
  authWatcher,
  logoutWatcher,
  refreshTokenWatcher,
  forgotPasswordWatcher,
  setCurrentUserInfoWatcher,
} from "./model/system/systemSaga";
import {
  dStoresSetDataWatcher,
  dStoresEditDataWatcher,
} from "./model/dashboardStores/dStoresSaga";
import {
  dProductsAddItemWatcher,
  dProductsSetDataWatcher,
  dProductsEditItemWatcher,
  dProductsDeleteAllWatcher,
  dProductsChangeVATWatcher,
  dProductsDeleteItemWatcher,
  dProductsBulkDeleteWatcher,
  dProductsExportDataWatcher,
  dProductsImportDataWatcher,
  dProductsRecoverItemWatcher,
  dProductsBulkOffSaleWatcher,
  dProductsSaveTemplateWatcher,
  dProductsBulkRecoverItemWatcher,
  dProductsDeleteAllFromStoreWatcher,
  dProductsBulkChangeCategoryWatcher,
  dProductsBulkDeleteFromStoresWatcher,
  dProductsRestoreDeletedItemWithNewDataWatcher,
} from "./model/dashboardProducts/dProductsSaga";
import {
  dCategoriesAddDataWatcher,
  dCategoriesSetDataWatcher,
  dCategoriesEditDataWatcher,
  dCategoriesDeleteDataWatcher,
  dCategoriesBulkDeleteWatcher,
  dCategoriesBulkRecoverWatcher,
} from "./model/dashboardCategories/dCategoriesSaga";
import {
  dZReportsSetDataWatcher,
  dZReportsExportDataWatcher,
  dZReportsExportSalesWatcher,
} from "./model/dashboardZreports/dZReportsSaga";
import {
  dSalesSetDataWatcher,
  dSalesExportDataWatcher,
  dSalesSelectedExportDataWatcher,
} from "./model/dashboardSales/dSalesSaga";
import {
  dTicketsAddDataWatcher,
  dTicketsEditDataWatcher,
  dTicketsSetDataWatcher,
  dTicketsSetMerchantDataWatcher,
  dTicketsAddResponseDataWatcher,
  dTicketsSetResponseViewedWatcher,
} from "./model/dashboardTickets/dTicketsSaga";
import {
  dUsersAddItemWatcher,
  dUsersSetDataWatcher,
  dUsersEditDataWatcher,
  dUsersDeleteItemWatcher,
  dUsersBulkRecoverWatcher,
  dUsersChangePINItemWatcher,
  dUsersChangePasswordDataWatcher,
} from "./model/dashboardUsers/dUsersSaga";
import {
  dPosConfigurationAddItemWatcher,
  dPosConfigurationEditItemWatcher,
  dPosConfigurationSetDataWatcher,
  dPosConfigurationDeleteItemWatcher,
  dPosConfigurationGetFullItemWatcher,
} from "./model/dashboardPosConfiguration/dPosConfigurationSaga";
import {
  dOrdersAddDataWatcher,
  dOrdersSetDataWatcher,
  dOrdersEditDataWatcher,
  dOrdersExportDataWatcher,
  dOrdersBulkChangeStatusWatcher,
  dOrdersSetFilteredProductWatcher,
  dOrdersSetFullDetailsItemWatcher,
  dOrdersSetSaleDetailsItemWatcher,
  dOrdersSelectedExportDataWatcher,
} from "./model/dashboardOrders/dOrdersSaga";
import {
  dStatisticsSetDataWatcher,
  dStatisticsSetDailyDataWatcher,
} from "./model/dashboardStatistics/dStatisticsSaga";
import {
  settingsAddCallbackWatcher,
  settingsDeleteCallbackWatcher,
  settingsEditUrlCallbackWatcher,
  settingsSetCallbackDataWatcher,
} from "./model/dashboardSettings/settingsSaga.ts";
import {
  dEpsSetDataWatcher,
  dEpsAddPosSettingsForTerminalWatcher,
  dEpsDisconnectTerminalItemFromEpsWatcher,
  dEpsBulkDisconnectTerminalsFromEpsWatcher,
} from "./model/dashboardEPS/dEpsSaga";
import {
  dEdmProductsAddItemWatcher,
  dEdmProductsSetDataWatcher,
  dEdmProductsEditItemWatcher,
} from "./model/dashboardEdmProducts/dEdmProductsSaga";
import "./styles/scss/common-styles.scss";

i18n.loadLocaleData({
  en: { plurals: en },
  uz: { plurals: uz },
  ru: { plurals: ru },
});

function sentryInit() {
  if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.init({
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      dsn: process.env.REACT_APP_SENTRY_DSN,
      environment: process.env.REACT_APP_SENTRY_ENV,
      integrations: [new Sentry.Replay(), new Sentry.BrowserTracing()],
    });
  }
}

sentryInit();

const loadMessages = async (language: LANGUAGE) => {
  const { messages } = await import(`locales/${language}/messages`);

  i18n.load(language, messages);
  i18n.activate(language);
};

const App: React.FC = ({ children }) => {
  const dispatch = useDispatch();

  const auth = useSelector((state: any) => state.system.auth);

  useCheckAuth(auth);

  if (!auth) {
    const envLanguage = process.env.REACT_APP_LANGUAGE;

    if (Object.values(LANGUAGE).includes(envLanguage)) {
      dispatch(languageAC(envLanguage));
    }
  }

  const language = useSelector(
    (state: { system: ISystemState }) => state.system.language,
  );

  useEffect(() => {
    if (language) {
      loadMessages(language).catch(console.error);
    }
  }, [language]);

  return <>{children}</>;
};

function* rootSaga() {
  yield spawn(authWatcher);
  yield spawn(refreshTokenWatcher);
  yield spawn(logoutWatcher);
  yield spawn(forgotPasswordWatcher);
  yield spawn(setCurrentUserInfoWatcher);

  yield spawn(dStoresSetDataWatcher);
  yield spawn(dStoresEditDataWatcher);

  yield spawn(dCategoriesSetDataWatcher);
  yield spawn(dCategoriesEditDataWatcher);
  yield spawn(dCategoriesAddDataWatcher);
  yield spawn(dCategoriesDeleteDataWatcher);
  yield spawn(dCategoriesBulkDeleteWatcher);
  yield spawn(dCategoriesBulkRecoverWatcher);

  yield spawn(dProductsSetDataWatcher);
  yield spawn(dProductsEditItemWatcher);
  yield spawn(dProductsAddItemWatcher);
  yield spawn(dProductsDeleteItemWatcher);
  yield spawn(dProductsRecoverItemWatcher);
  yield spawn(dProductsBulkRecoverItemWatcher);
  yield spawn(dProductsBulkChangeCategoryWatcher);
  yield spawn(dProductsBulkOffSaleWatcher);
  yield spawn(dProductsBulkDeleteWatcher);
  yield spawn(dProductsBulkDeleteFromStoresWatcher);
  yield spawn(dProductsExportDataWatcher);
  yield spawn(dProductsImportDataWatcher);
  yield spawn(dProductsSaveTemplateWatcher);
  yield spawn(dProductsDeleteAllWatcher);
  yield spawn(dProductsChangeVATWatcher);
  yield spawn(dProductsRestoreDeletedItemWithNewDataWatcher);
  yield spawn(dProductsDeleteAllFromStoreWatcher);

  if (edmIsEnabled) {
    yield spawn(dEdmProductsSetDataWatcher);
    yield spawn(dEdmProductsAddItemWatcher);
    yield spawn(dEdmProductsEditItemWatcher);
  }

  yield spawn(dZReportsSetDataWatcher);
  yield spawn(dZReportsExportDataWatcher);
  yield spawn(dZReportsExportSalesWatcher);

  yield spawn(dCashDesksSetDataWatcher);

  yield spawn(dHRecordsSetDataWatcher);

  yield spawn(dSalesExportDataWatcher);
  yield spawn(dSalesSelectedExportDataWatcher);

  yield spawn(dSalesSetDataWatcher);
  yield spawn(dSalesArchiveSetDataWatcher);

  yield spawn(dTicketsSetDataWatcher);
  yield spawn(dTicketsEditDataWatcher);
  yield spawn(dTicketsAddResponseDataWatcher);
  yield spawn(dTicketsAddDataWatcher);
  yield spawn(dTicketsSetResponseViewedWatcher);
  yield spawn(dTicketsSetMerchantDataWatcher);

  yield spawn(dUsersSetDataWatcher);
  yield spawn(dUsersDeleteItemWatcher);
  yield spawn(dUsersEditDataWatcher);
  yield spawn(dUsersBulkRecoverWatcher);
  yield spawn(dUsersAddItemWatcher);
  yield spawn(dUsersChangePasswordDataWatcher);
  yield spawn(dUsersChangePINItemWatcher);

  yield spawn(dPosConfigurationSetDataWatcher);
  yield spawn(dPosConfigurationAddItemWatcher);
  yield spawn(dPosConfigurationEditItemWatcher);
  yield spawn(dPosConfigurationDeleteItemWatcher);
  yield spawn(dPosConfigurationGetFullItemWatcher);

  yield spawn(dRefundsSetDataWatcher);
  yield spawn(dRefundsArchiveSetDataWatcher);

  yield spawn(dOrdersSetDataWatcher);
  yield spawn(dOrdersArchiveSetDataWatcher);

  yield spawn(dOrdersSetFilteredProductWatcher);
  yield spawn(dOrdersAddDataWatcher);
  yield spawn(dOrdersEditDataWatcher);
  yield spawn(dOrdersSetSaleDetailsItemWatcher);
  yield spawn(dOrdersBulkChangeStatusWatcher);
  yield spawn(dOrdersSetFullDetailsItemWatcher);
  yield spawn(dOrdersExportDataWatcher);
  yield spawn(dOrdersSelectedExportDataWatcher);

  yield spawn(dTransferCashDesksSetDataWatcher);

  yield spawn(dStatisticsSetDataWatcher);
  yield spawn(dStatisticsSetDailyDataWatcher);

  yield spawn(settingsSetCallbackDataWatcher);
  yield spawn(settingsAddCallbackWatcher);
  yield spawn(settingsDeleteCallbackWatcher);
  yield spawn(settingsEditUrlCallbackWatcher);

  yield spawn(dEpsSetDataWatcher);
  yield spawn(dEpsAddPosSettingsForTerminalWatcher);
  yield spawn(dEpsDisconnectTerminalItemFromEpsWatcher);
  yield spawn(dEpsBulkDisconnectTerminalsFromEpsWatcher);

  yield spawn(dEncashSetDataWatcher);
}

sagaMiddleware.run(rootSaga);

ReactDOM.render(
  <React.StrictMode>
    <Provider store={store}>
      <App>
        <I18nProvider i18n={i18n}>
          <MainRouter />
        </I18nProvider>
      </App>
    </Provider>
    {process.env.REACT_APP_IS_YA_METRIC_ON === "true" && (
      <YMInitializer accounts={[96378139]} options={{ webvisor: true }} />
    )}
  </React.StrictMode>,
  document.getElementById("root"),
);

serviceWorker.unregister();
